import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import postRobot from 'post-robot';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { IconExternalLink, IconPencil, IconWand } from '@tabler/icons-react';
import mergeWith from 'lodash.mergewith';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { Tooltip } from '@mui/material';

import { getNetworkError } from 'common/utils';

import { Spinner, SwitchInput } from 'ui';

import appSettings from '../../../app-settings';
import { useCampaigns } from '../../../store/campaigns/hooks';
import { useAccount } from '../../../store/account/hooks';
import { usePages } from '../../../store/pages/hooks';
import {
  duplicateDefaultPage,
  getPage,
  getTemplate,
  updateCampaign,
  updateTemplate,
  uploadHtml,
  uploadPageImageFromUrl,
  upsertPage,
} from '../../../api';
import { getPageImagesUrls, getPageUrl, nameToSlug } from '../../../core/utils';
import PageEditor from '../../page-editor/PageEditor';
import Button from '../../buttons/Button';

const ARTIFICIAL_INTELLIGENCE_URL = `${appSettings.baseUrl}/artificial-intelligence/completions`;

// Regex matching donation page URLs: https://{subdomain}.{donationPageDomain}{/optional-slug}?...
const getDonationPageUrlRegex = () => {
  const escapedDomain = appSettings.donationPageDomain.replace(/\./g, '\\.');
  return new RegExp(`(https?://[^/]+\\.${escapedDomain})((?:/[^?"'\\s&]*)?)`, 'g');
};

/**
 * Replace slug portion of donation page URLs in HTML string.
 * Also updates base64-encoded data-u-link-value attributes.
 */
const replaceDonationPageLinksInHtml = (html, newSlug) => {
  const urlRegex = getDonationPageUrlRegex();

  // Replace plain href URLs
  let updated = html.replace(urlRegex, (match, origin) => `${origin}/${newSlug}`);

  // Replace data-u-link-value base64 attributes
  updated = updated.replace(/data-u-link-value="([^"]+)"/g, (fullMatch, b64Value) => {
    try {
      const decoded = JSON.parse(atob(b64Value));
      if (decoded.attrs?.href) {
        decoded.attrs.href = decoded.attrs.href.replace(urlRegex, (m, origin) => `${origin}/${newSlug}`);
      }
      if (decoded.values?.slug !== undefined) {
        decoded.values.slug = `/${newSlug}`;
      }
      return `data-u-link-value="${btoa(JSON.stringify(decoded))}"`;
    } catch {
      return fullMatch;
    }
  });

  return updated;
};

/**
 * Recursively walk a design JSON object and replace donation page link slugs.
 */
const replaceDonationPageLinksInDesign = (obj, newSlug) => {
  if (!obj || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => replaceDonationPageLinksInDesign(item, newSlug));
  }

  const urlRegex = getDonationPageUrlRegex();
  const result = {};

  for (const [key, value] of Object.entries(obj)) {
    if (key === 'href' && typeof value === 'object' && value?.name === 'static_donation_page_link') {
      result[key] = {
        ...value,
        values: { ...value.values, slug: `/${newSlug}` },
        attrs: value.attrs
          ? {
              ...value.attrs,
              href: value.attrs.href?.replace(urlRegex, (m, origin) => `${origin}/${newSlug}`),
            }
          : value.attrs,
      };
    } else if (key === 'text' && typeof value === 'string' && value.includes(appSettings.donationPageDomain)) {
      result[key] = replaceDonationPageLinksInHtml(value, newSlug);
    } else {
      result[key] = replaceDonationPageLinksInDesign(value, newSlug);
    }
  }

  return result;
};

const DonationPageForm = ({ disabled }) => {
  const { campaignDetails, updateCampaignDetails, setError, setCampaignTemplate } = useCampaigns();
  const { account } = useAccount();
  const { pageList } = usePages();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const [creating, setCreating] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [adapting, setAdapting] = useState(false);
  const [pageData, setPageData] = useState(null);
  const [iframeReady, setIframeReady] = useState(false);
  const pollIntervalRef = useRef(null);
  const lastSavedDataRef = useRef(null);

  const donationPage = campaignDetails.donationPage || {};
  const { enabled, slug } = donationPage;
  const pageReady = enabled && !!slug;

  const pageUrl = pageReady ? getPageUrl({ account, slug }) : '';
  const editorUrl = `${appSettings.donationPageEditorUrl}/simple?customerSupportEmail=${encodeURIComponent(account?.customerSupportEmail || '')}`;

  const loadPageData = useCallback(async () => {
    if (!slug) return;
    try {
      const data = await getPage(slug);
      setPageData(data || null);
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [slug]);

  useEffect(() => {
    if (pageReady) {
      loadPageData();
    }
  }, [pageReady, loadPageData]);

  const generateAiContent = async (slug) => {
    const { templateId, version } = campaignDetails.template;
    if (!templateId) return null;

    const template = await getTemplate(templateId, version);

    const prompt = `
      Analyze the following HTML email and extract content suitable for a donation page.
      Return only a parsable JSON object (with no wrapping text or formatting markdown) with these keys:
      - heroTitle: A short, compelling headline (max 10 words) derived from the email subject or main message
      - heroDescription: A motivating description (2 short paragraphs with 1-3 sentences each) that encourages donations, based on the email content. This should use HTML p tags for paragraph breaks but no other formatting.
      - heroImageUrl: The URL of the most prominent non-logo image from the email, or empty string if none found
      Subject: ${campaignDetails.emailConfig.subject}
      Preview Text: ${campaignDetails.emailConfig.previewText}
      HTML email content:
      \`\`\`
      ${template.html}
      \`\`\`
    `;

    const result = await axios.post(ARTIFICIAL_INTELLIGENCE_URL, { prompt });
    const { completion } = result.data;
    const { heroTitle, heroDescription, heroImageUrl } = JSON.parse(completion);

    const aiContent = {
      text: {
        callToAction: heroTitle,
        motivatingDescription: heroDescription,
      },
    };

    if (heroImageUrl) {
      await uploadPageImageFromUrl({ slug, imageKey: 'heroImage', imageUrl: heroImageUrl });
      aiContent.images = getPageImagesUrls({
        images: { heroImage: { url: '' } },
        tenantId: account?.tenantId,
        slug,
      });
    }

    return aiContent;
  };

  const handleAdaptEmail = async () => {
    try {
      if (!editorRef.current?.contentWindow) return;

      setAdapting(true);

      const aiContent = await generateAiContent(slug);
      if (!aiContent) return;

      // Get current editor state, merge AI content, and re-initialize
      const response = await postRobot.send(editorRef.current.contentWindow, 'saveAndExit');
      const currentState = response.data || {};

      const updatedState = mergeWith({}, currentState, aiContent);
      await postRobot.send(editorRef.current.contentWindow, 'initEditor', { initialState: updatedState });
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
    } finally {
      setAdapting(false);
    }
  };

  const handleToggle = async (checked) => {
    if (disabled) return;

    if (checked && !slug) {
      // First time enabling — duplicate the default page, merging AI content if available
      setCreating(true);
      try {
        const slug = nameToSlug(`campaign-${campaignDetails.emailConfig.subject}`);
        let aiContent = null;

        if (campaignDetails.template.templateId) {
          try {
            aiContent = await generateAiContent(slug);
          } catch (err) {
            Sentry.captureException(err);
            // Continue without AI content if generation fails
          }
        }
        const result = await duplicateDefaultPage(campaignDetails.name, slug, aiContent, 'campaign');
        updateCampaignDetails({
          donationPage: { enabled: true, slug, pageId: result.pageId },
        });
        const { campaignId, tempId, segmentId, ...data } = campaignDetails;
        await updateCampaign(campaignDetails.campaignId, {
          ...data,
          donationPage: { enabled: true, slug, pageId: result.pageId },
        });

        // Replace donation page links in the email template to point to the new campaign page
        if (campaignDetails.template.templateId) {
          try {
            const { templateId, version } = campaignDetails.template;
            const template = await getTemplate(templateId, version);
            const design = typeof template.design === 'string' ? JSON.parse(template.design) : template.design;
            const updatedDesign = replaceDonationPageLinksInDesign(design, result.slug);
            const updatedHtml = replaceDonationPageLinksInHtml(template.html, result.slug);
            const saveResult = await updateTemplate(templateId, version, updatedDesign, campaignDetails.emailConfig);
            if (saveResult.uploadUrl) {
              await uploadHtml(saveResult.uploadUrl, updatedHtml);
            }
            setCampaignTemplate({
              ...template,
              html: updatedHtml,
              design: updatedDesign,
            });
          } catch (err) {
            Sentry.captureException(err);
            // Continue even if link replacement fails
          }
        }
      } catch (err) {
        Sentry.captureException(err);
        setError(getNetworkError(err));
      }
      setCreating(false);
    } else {
      // Toggle existing page on/off
      updateCampaignDetails({ donationPage: { enabled: checked } });
    }
  };

  const initializeEditor = useCallback(async () => {
    try {
      if (!pageData || !iframeReady || !editorRef.current?.contentWindow) return;

      const baseUrl = pageUrl.replace(slug, '');
      const gtmId = pageList.find((page) => page.slug === 'default')?.content?.gtmId || '';

      const content = pageData?.content
        ? { ...pageData.content, metadata: { name: pageData.name || '', slug, baseUrl } }
        : { gtmId, metadata: { baseUrl } };

      const initialState = account?.logoUrl
        ? mergeWith({}, { images: { headerImage: { url: account.logoUrl } } }, content)
        : content;

      await postRobot.send(editorRef.current.contentWindow, 'initEditor', { initialState });

      // Poll the editor periodically for changes via saveAndExit
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      lastSavedDataRef.current = initialState;

      pollIntervalRef.current = setInterval(async () => {
        try {
          if (!editorRef.current?.contentWindow || !account) return;
          const response = await postRobot.send(editorRef.current.contentWindow, 'saveAndExit');
          if (!response.data) return;

          if (isEqual(response.data, lastSavedDataRef.current)) return;

          lastSavedDataRef.current = response.data;
          await upsertPage({
            tenantId: account.tenantId,
            slug,
            content: response.data,
            action: 'update',
          });
        } catch (err) {
          Sentry.captureException(err);
        }
      }, 5000);

      setEditorLoading(false);
    } catch (error) {
      Sentry.captureException(error);
      setError('Something went wrong while initializing the donation page editor.');
    }
  }, [pageData, iframeReady, pageUrl, slug, pageList, account, setError]);

  useEffect(() => {
    initializeEditor();
  }, [initializeEditor]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  const handlePreview = () => {
    if (pageUrl) window.open(pageUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6">
      <SwitchInput
        label="Customize Donation Page for this campaign"
        checked={enabled || false}
        onChange={handleToggle}
        disabled={enabled || disabled || creating}
      />

      {creating && (
        <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
          <Spinner />
          Adapting email to donation page...
        </div>
      )}

      {pageReady && (
        <div className="space-y-4">
          <div className="flex items-center justify-between space-x-2">
            {campaignDetails.template.templateId && (
              <Tooltip
                placement="bottom"
                title="Adapt your email content to populate the donation page hero image, call to action, and description."
              >
                <div>
                  <Button
                    title="Adapt Email to Donation Page"
                    onClick={handleAdaptEmail}
                    LeftIcon={IconWand}
                    size="sm"
                    disabled={disabled || adapting}
                  />
                </div>
              </Tooltip>
            )}
            <div className="flex items-center space-x-2">
              <Button title="Preview" onClick={handlePreview} LeftIcon={IconExternalLink} size="sm" />
              <Button
                title="Full Editor"
                onClick={() =>
                  navigate(`/donation-pages/${slug}`, {
                    state: { fromCampaignId: campaignDetails.campaignId },
                  })
                }
                LeftIcon={IconPencil}
                size="sm"
              />
            </div>
          </div>

          <div className="h-[500px] overflow-auto">
            <PageEditor
              ref={editorRef}
              pageUrl={editorUrl}
              loading={editorLoading}
              onLoad={() => setIframeReady(true)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

DonationPageForm.propTypes = {
  disabled: PropTypes.bool,
};

export default DonationPageForm;
